import React, { useEffect, useState } from "react";
import authAxios from "../auth/authAxios";
import { useMsal } from "@azure/msal-react";

interface UserEngagement {
  total_active_users: number;
  lessons_completed: number;
  average_score: number;
}

interface LessonDifficulty {
  lesson_id: string;
  title: string;
  avg_score: number;
}

interface ChallengeAvgScore {
  challenge_id: string;
  challenge_title: string;
  avg_score: number;
}

interface PromptTrend {
  week_start: string; // ISO date string
  avg_prompt_length: number;
  avg_score: number;
}

interface UserListItem {
  user_id: string;       // Use user_id here, not email
  display_name: string;
}

const Analytics = () => {
  const { accounts } = useMsal();
  const user = accounts[0];

  const [hasRole, setHasRole] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [userList, setUserList] = useState<UserListItem[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>("");

  const [userEngagement, setUserEngagement] = useState<UserEngagement | null>(null);
  const [lessonDifficulty, setLessonDifficulty] = useState<LessonDifficulty[]>([]);
  const [challengeAvgScores, setChallengeAvgScores] = useState<ChallengeAvgScore[]>([]);
  const [promptTrends, setPromptTrends] = useState<PromptTrend[]>([]);

  useEffect(() => {
    if (!user) {
      setHasRole(false);
      setLoading(false);
      return;
    }

    const roles = (user.idTokenClaims?.roles || []) as string[];
    if (!roles.includes("ChatGPT.Admin")) {
      setHasRole(false);
      setLoading(false);
      return;
    }
    setHasRole(true);

    async function fetchUserList() {
      try {
        const res = await authAxios.get<UserListItem[]>("/api/users");
        setUserList(res.data);
      } catch (e) {
        console.error("Failed to load user list", e);
      }
    }

    fetchUserList();
  }, [user]);

  useEffect(() => {
    if (!hasRole) return;

    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const params: Record<string, string> = {};
        if (selectedUserId) params.user_id = selectedUserId;  // pass user_id, not email

        const [
          engagementRes,
          difficultyRes,
          avgScoresRes,
          promptTrendsRes,
        ] = await Promise.all([
          authAxios.get<UserEngagement>("/api/analytics/user-engagement", { params }),
          authAxios.get<LessonDifficulty[]>("/api/analytics/lesson-difficulty", { params }),
          authAxios.get<ChallengeAvgScore[]>("/api/analytics/challenge-average-scores", { params }),
          authAxios.get<PromptTrend[]>("/api/analytics/prompt-trends", { params }),
        ]);

        setUserEngagement(engagementRes.data);
        setLessonDifficulty(difficultyRes.data);
        setChallengeAvgScores(avgScoresRes.data);
        setPromptTrends(promptTrendsRes.data);
      } catch (e) {
        setError("Failed to load analytics data.");
        console.error(e);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [selectedUserId, hasRole]);

  if (loading) return <p className="p-6 text-center text-white">Loading analytics...</p>;
  if (!hasRole) return <p className="p-6 text-center text-red-500">Access denied. You do not have the required role to view analytics.</p>;
  if (error) return <p className="p-6 text-center text-red-500">{error}</p>;

  return (
    <main className="max-w-7xl mx-auto p-6 text-white">
      <h1 className="text-3xl font-bold mb-8">Analytics Dashboard</h1>

      {/* User Filter Dropdown */}
      <div className="mb-8 max-w-xs">
        <label htmlFor="userSelect" className="block mb-1 font-semibold">
          Filter by User
        </label>
        <select
          id="userSelect"
          className="w-full rounded px-3 py-2 text-black"
          value={selectedUserId}
          onChange={(e) => setSelectedUserId(e.target.value)}
        >
          <option value="">All Users</option>
          {userList.map(({ user_id, display_name }) => (
            <option key={user_id} value={user_id}>
              {display_name}
            </option>
          ))}
        </select>
      </div>

      {/* User Engagement */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-4">User Engagement</h2>
        {userEngagement && (
          <ul className="grid grid-cols-3 gap-6 max-w-md">
            <li className="bg-bg-panel p-4 rounded shadow text-center">
              <div className="text-4xl font-bold">{userEngagement.total_active_users}</div>
              <div>Total Active Users (30 days)</div>
            </li>
            <li className="bg-bg-panel p-4 rounded shadow text-center">
              <div className="text-4xl font-bold">{userEngagement.lessons_completed}</div>
              <div>Lessons Completed (30 days)</div>
            </li>
            <li className="bg-bg-panel p-4 rounded shadow text-center">
              <div className="text-4xl font-bold">{userEngagement.average_score.toFixed(2)}</div>
              <div>Average Score</div>
            </li>
          </ul>
        )}
      </section>

      {(userEngagement && userEngagement.lessons_completed > 0) && (
        <>
        {/* Lesson Difficulty */}
        <section className="mb-10">
            <h2 className="text-xl font-semibold mb-4">Lesson Difficulty (Avg Scores)</h2>
            <table className="w-full text-left table-auto border-collapse border border-gray-600 rounded overflow-hidden">
            <thead className="bg-primary/80">
                <tr>
                <th className="px-4 py-2 border border-gray-600">Lesson</th>
                <th className="px-4 py-2 border border-gray-600">Avg Score</th>
                </tr>
            </thead>
            <tbody>
                {lessonDifficulty.map((lesson) => (
                <tr key={lesson.lesson_id} className="hover:bg-bg-panel">
                    <td className="border border-gray-600 px-4 py-2">{lesson.title}</td>
                    <td className="border border-gray-600 px-4 py-2">{lesson.avg_score.toFixed(2)}</td>
                </tr>
                ))}
            </tbody>
            </table>
        </section>

        {/* Challenge Average Scores */}
        <section className="mb-10">
            <h2 className="text-xl font-semibold mb-4">Challenge Average Scores</h2>
            <table className="w-full text-left table-auto border-collapse border border-gray-600 rounded overflow-hidden">
            <thead className="bg-primary/80">
                <tr>
                <th className="px-4 py-2 border border-gray-600">Challenge</th>
                <th className="px-4 py-2 border border-gray-600">Avg Score</th>
                </tr>
            </thead>
            <tbody>
                {challengeAvgScores.map((c) => (
                <tr key={c.challenge_id} className="hover:bg-bg-panel">
                    <td className="border border-gray-600 px-4 py-2">{c.challenge_title}</td>
                    <td className="border border-gray-600 px-4 py-2">{c.avg_score.toFixed(2)}</td>
                </tr>
                ))}
            </tbody>
            </table>
        </section>

        {/* Prompt Trends */}
        <section className="mb-10">
            <h2 className="text-xl font-semibold mb-4">Prompt Trends (Last 8 Weeks)</h2>
            <table className="w-full text-left table-auto border-collapse border border-gray-600 rounded overflow-hidden">
            <thead className="bg-primary/80">
                <tr>
                <th className="px-4 py-2 border border-gray-600">Week Starting</th>
                <th className="px-4 py-2 border border-gray-600">Avg Prompt Length</th>
                <th className="px-4 py-2 border border-gray-600">Avg Score</th>
                </tr>
            </thead>
            <tbody>
                {promptTrends.map((trend) => (
                <tr key={trend.week_start} className="hover:bg-bg-panel">
                    <td className="border border-gray-600 px-4 py-2">{new Date(trend.week_start).toLocaleDateString()}</td>
                    <td className="border border-gray-600 px-4 py-2">{trend.avg_prompt_length.toFixed(0)}</td>
                    <td className="border border-gray-600 px-4 py-2">{trend.avg_score.toFixed(2)}</td>
                </tr>
                ))}
            </tbody>
            </table>
        </section>
        </>
      )}
    </main>
  );
};

export default Analytics;
